// frontend/src/features/livestock/useZoomGestures.ts
// The pointer side of the compare page's synced zoom
// (docs/plans/2026-10-29-tank-shot-framing-design.md, 9), shared by the slider
// and side by side:
// - two fingers pinch (zoom about their midpoint) and move together (pan);
// - ctrl+wheel, which is also how a trackpad pinch arrives, zooms about the
//   pointer; a plain wheel is left to the page;
// - while zoomed, one pointer that `panWithOnePointer` allows drags the view,
//   and if it barely moves it is a tap (onTap);
// - opt-in for the photo viewer: a plain wheel pans while zoomed (wheelPans),
//   and two quick taps in one place at any scale are a double tap (onDoubleTap).
// The handlers return true when they took the event, so the caller (the
// slider's handle drag) leaves it alone. onPinchStart lets the caller drop a
// drag that a second finger interrupted.

import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';
import { panZoom, zoomAt, type CompareZoom, type ZoomLimits } from './compareZoom';
import { wheelPixels } from './wheelDelta';

/** Movement before one pointer becomes a pan rather than a tap (as the slider's drag). */
const PAN_THRESHOLD_PX = 8;
/** A wheel notch zooms by at most this factor; a trackpad pinch sends many small ones. */
const WHEEL_STEP_MAX = 50;
const WHEEL_SCALE = 0.01;
/** Two taps this close in time and place are a double tap. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 24;

interface Point {
  x: number;
  y: number;
}

interface Tap extends Point {
  time: number;
}

type Mode =
  | { kind: 'none' }
  | { kind: 'pinch' }
  | { kind: 'pan'; started: boolean; start: Point }
  /** A pinch ended with a finger still down: ignore it until it lifts. */
  | { kind: 'spent' };

export interface ZoomGestureOptions {
  ref: RefObject<HTMLElement>;
  zoom: CompareZoom;
  onZoomChange: (zoom: CompareZoom) => void;
  /** Whether a single pointer pressed here drags the view (only ever while zoomed). */
  panWithOnePointer?: (event: PointerEvent<HTMLElement>) => boolean;
  onPinchStart?: () => void;
  /** A single-pointer press that pans when dragged, let go without moving. */
  onTap?: (event: PointerEvent<HTMLElement>) => void;
  /** Full screen: also stop Safari's own pinch zoom (its gesture events). */
  preventBrowserZoom?: boolean;
  /** The maximum scale and the picture's box; see compareZoom. */
  limits?: ZoomLimits;
  /** While zoomed, a plain wheel (or a trackpad's two-finger scroll) pans. */
  wheelPans?: boolean;
  /** Two quick taps or clicks in one place, at any scale. */
  onDoubleTap?: (event: PointerEvent<HTMLElement>) => void;
}

export interface ZoomGestureHandlers {
  pointerDown: (event: PointerEvent<HTMLElement>) => boolean;
  pointerMove: (event: PointerEvent<HTMLElement>) => boolean;
  /** Pointer up or cancel; `tap` is false for a cancel. */
  pointerEnd: (event: PointerEvent<HTMLElement>, tap: boolean) => boolean;
  /** The element lost the pointer (its capture was taken, or it left): stop counting it. */
  forget: (pointerId: number) => void;
}

export function useZoomGestures({
  ref,
  zoom,
  onZoomChange,
  panWithOnePointer,
  onPinchStart,
  onTap,
  preventBrowserZoom = false,
  limits,
  wheelPans = false,
  onDoubleTap,
}: ZoomGestureOptions): ZoomGestureHandlers {
  const pointers = useRef(new Map<number, Point>());
  const mode = useRef<Mode>({ kind: 'none' });
  // A single pointer that might be a tap, and the last tap (for a double tap)
  const tapStart = useRef<Point | null>(null);
  const lastTap = useRef<Tap | null>(null);
  // The latest zoom, callback and limits: several moves can land before a render
  const latest = useRef({ zoom, onZoomChange, limits });
  latest.current = { zoom, onZoomChange, limits };

  const change = (next: CompareZoom): void => {
    latest.current.zoom = next;
    latest.current.onZoomChange(next);
  };

  const rect = (): DOMRect | null => {
    const box = ref.current?.getBoundingClientRect();
    return box && box.width > 0 && box.height > 0 ? box : null;
  };

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent): void => {
      const box = element.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) return;
      if (!event.ctrlKey) {
        if (!wheelPans || latest.current.zoom.scale <= 1) return;
        event.preventDefault();
        const moved = wheelPixels(event, box);
        change(
          panZoom(
            latest.current.zoom,
            -moved.x / box.width,
            -moved.y / box.height,
            latest.current.limits
          )
        );
        return;
      }
      event.preventDefault();
      // In pixels, also when the wheel reports lines or pages (Firefox)
      const moved = wheelPixels(event, box).y;
      const delta = Math.max(-WHEEL_STEP_MAX, Math.min(WHEEL_STEP_MAX, moved));
      const u = (event.clientX - box.left) / box.width;
      const v = (event.clientY - box.top) / box.height;
      change(
        zoomAt(latest.current.zoom, Math.exp(-delta * WHEEL_SCALE), u, v, latest.current.limits)
      );
    };
    const stop = (event: Event): void => event.preventDefault();
    element.addEventListener('wheel', onWheel, { passive: false });
    if (preventBrowserZoom) {
      element.addEventListener('gesturestart', stop);
      element.addEventListener('gesturechange', stop);
    }
    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('gesturestart', stop);
      element.removeEventListener('gesturechange', stop);
    };
    // `change` reads everything through refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, preventBrowserZoom, wheelPans]);

  const capture = (pointerId: number): void => {
    try {
      ref.current?.setPointerCapture(pointerId);
    } catch {
      // The pointer is already gone.
    }
  };

  const pointerDown = (event: PointerEvent<HTMLElement>): boolean => {
    if (event.pointerType === 'mouse') {
      if (event.button !== 0) return false;
      // A mouse is one pointer: whatever is still counted was let go elsewhere.
      pointers.current.clear();
      mode.current = { kind: 'none' };
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    tapStart.current = pointers.current.size === 1 ? { x: event.clientX, y: event.clientY } : null;
    if (pointers.current.size === 2) {
      mode.current = { kind: 'pinch' };
      onPinchStart?.();
      pointers.current.forEach((_point, id) => capture(id));
      return true;
    }
    if (pointers.current.size > 2 || mode.current.kind !== 'none') return true;
    if (latest.current.zoom.scale > 1 && panWithOnePointer?.(event)) {
      mode.current = { kind: 'pan', started: false, start: { x: event.clientX, y: event.clientY } };
      return true;
    }
    return false;
  };

  const pointerMove = (event: PointerEvent<HTMLElement>): boolean => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return false;
    // A mouse moving with no button down was let go somewhere this element
    // didn't see: whatever it started is over (it must not pan on hover).
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      forget(event.pointerId);
      return false;
    }
    const now = { x: event.clientX, y: event.clientY };
    const start = tapStart.current;
    if (start && Math.hypot(now.x - start.x, now.y - start.y) >= PAN_THRESHOLD_PX) {
      tapStart.current = null;
    }
    const current = mode.current;
    const box = rect();
    if (current.kind === 'pinch' && box) {
      const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
      if (other) {
        const before = Math.hypot(previous.x - other.x, previous.y - other.y);
        const after = Math.hypot(now.x - other.x, now.y - other.y);
        const midBefore = { x: (previous.x + other.x) / 2, y: (previous.y + other.y) / 2 };
        const midAfter = { x: (now.x + other.x) / 2, y: (now.y + other.y) / 2 };
        let next = panZoom(
          latest.current.zoom,
          (midAfter.x - midBefore.x) / box.width,
          (midAfter.y - midBefore.y) / box.height,
          latest.current.limits
        );
        if (before > 0 && after > 0) {
          next = zoomAt(
            next,
            after / before,
            (midAfter.x - box.left) / box.width,
            (midAfter.y - box.top) / box.height,
            latest.current.limits
          );
        }
        change(next);
      }
    } else if (current.kind === 'pan' && box) {
      if (!current.started) {
        const moved = Math.hypot(now.x - current.start.x, now.y - current.start.y);
        if (moved < PAN_THRESHOLD_PX) {
          pointers.current.set(event.pointerId, now);
          return true;
        }
        current.started = true;
        capture(event.pointerId);
      }
      change(
        panZoom(
          latest.current.zoom,
          (now.x - previous.x) / box.width,
          (now.y - previous.y) / box.height,
          latest.current.limits
        )
      );
    }
    pointers.current.set(event.pointerId, now);
    return current.kind !== 'none';
  };

  /** A lone pointer let go where it went down: the second of two quick ones is a double tap. */
  const noteTap = (event: PointerEvent<HTMLElement>): void => {
    if (!onDoubleTap) return;
    const at: Tap = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    const last = lastTap.current;
    if (
      last &&
      at.time - last.time <= DOUBLE_TAP_MS &&
      Math.hypot(at.x - last.x, at.y - last.y) <= DOUBLE_TAP_PX
    ) {
      lastTap.current = null;
      onDoubleTap(event);
    } else {
      lastTap.current = at;
    }
  };

  const pointerEnd = (event: PointerEvent<HTMLElement>, tap: boolean): boolean => {
    if (!pointers.current.delete(event.pointerId)) return mode.current.kind !== 'none';
    const wasTap = tap && tapStart.current !== null && pointers.current.size === 0;
    tapStart.current = null;
    if (wasTap) noteTap(event);
    else lastTap.current = null;
    const current = mode.current;
    if (current.kind === 'none') return false;
    if (current.kind === 'pan' && !current.started && tap) onTap?.(event);
    mode.current = pointers.current.size > 0 ? { kind: 'spent' } : { kind: 'none' };
    return true;
  };

  const forget = (pointerId: number): void => {
    if (!pointers.current.delete(pointerId)) return;
    tapStart.current = null;
    if (pointers.current.size === 0) mode.current = { kind: 'none' };
    else if (mode.current.kind === 'pinch') mode.current = { kind: 'spent' };
  };

  return { pointerDown, pointerMove, pointerEnd, forget };
}
